import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Network, Layers, ShieldCheck, Users } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import PillarsCarousel from "../components/PillarsCarousel";

const HERO_IMG =
  "https://images.unsplash.com/photo-1611036884458-6650ef4ccfdb?w=1600&q=85&auto=format&fit=crop";

const Home = () => {
  const { t } = useLanguage();
  const h = t.home;

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="border-b border-synergia-line" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-20 md:pt-28 pb-20 md:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <div className="eyebrow animate-fade-in">{h.eyebrow}</div>
            <h1 className="mt-6 font-montserrat font-semibold text-4xl sm:text-5xl lg:text-[64px] leading-[1.04] tracking-tight text-synergia-graphite text-balance animate-fade-up">
              {h.heroTitle}
            </h1>
            <div className="hero-rule mt-10" />
            <p
              className="mt-8 font-inter text-lg leading-relaxed text-synergia-graphite/75 max-w-2xl animate-fade-up"
              style={{ animationDelay: "120ms" }}
            >
              {h.heroSubtitle}
            </p>
            <div
              className="mt-10 flex flex-wrap items-center gap-5 animate-fade-up"
              style={{ animationDelay: "220ms" }}
            >
              <Link to="/contato" className="btn-primary" data-testid="hero-cta-primary">
                {h.heroCta1} <ArrowRight size={16} />
              </Link>
              <Link to="/solucoes" className="btn-secondary" data-testid="hero-cta-secondary">
                {h.heroCta2}
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5 hidden lg:block">
            <div className="relative">
              <div className="absolute -left-6 top-0 h-full w-px bg-synergia-line" />
              <img
                src={HERO_IMG}
                alt=""
                className="w-full h-[420px] object-cover grayscale"
                style={{ objectPosition: "center center" }}
                data-testid="hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ATUAÇÃO ESTRATÉGICA */}
      <section className="border-b border-synergia-line" data-testid="strategic-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-4">
            <div className="eyebrow">{h.strategicEyebrow}</div>
          </div>
          <div className="md:col-span-8">
            <h2 className="font-montserrat font-semibold text-3xl md:text-4xl leading-tight tracking-tight text-synergia-graphite max-w-2xl text-balance">
              {h.strategicTitle}
            </h2>
            <p className="mt-8 font-inter text-lg leading-relaxed text-synergia-graphite/75 max-w-3xl">
              {h.strategicBody}
            </p>
          </div>
        </div>
      </section>

      {/* 4 PILARES - CARROSSEL */}
      <section className="border-b border-synergia-line" data-testid="pillars-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mb-16">
            <div className="md:col-span-4">
              <div className="eyebrow">{h.pillarsEyebrow}</div>
            </div>
            <div className="md:col-span-8">
              <h2 className="font-montserrat font-semibold text-3xl md:text-4xl leading-tight tracking-tight text-synergia-graphite max-w-2xl">
                {h.pillarsTitle}
              </h2>
            </div>
          </div>

          <PillarsCarousel pillars={h.pillars} />
        </div>
      </section>

      {/* SETORES - CARDS 2x2 */}
      <section className="border-b border-synergia-line" data-testid="sectors-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mb-16">
            <div className="md:col-span-4">
              <div className="eyebrow">{h.sectorsEyebrow}</div>
            </div>
            <div className="md:col-span-8">
              <h2 className="font-montserrat font-semibold text-3xl md:text-4xl leading-tight tracking-tight text-synergia-graphite max-w-2xl">
                {h.sectorsTitle}
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8" data-testid="sectors-grid">
            {h.sectors.map((s, i) => (
              <div
                key={s.title}
                className="bg-synergia-greenLight border-l-2 border-synergia-green p-8 md:p-10 flex flex-col transition-colors duration-300 hover:bg-synergia-greenSoft"
                data-testid={`sector-${i}`}
              >
                <div className="font-montserrat font-semibold text-synergia-gold text-sm tracking-widest">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="mt-5 font-montserrat font-semibold text-xl md:text-2xl text-synergia-green leading-snug text-balance">
                  {s.title}
                </h3>
                <div className="h-[2px] w-12 bg-synergia-gold mt-6" />
                <p className="mt-6 font-inter text-base leading-relaxed text-synergia-graphite/80">
                  {s.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROJETOS & EXPERIÊNCIA */}
      <section className="border-b border-synergia-line" data-testid="projects-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-4">
            <div className="eyebrow">{h.projectsEyebrow}</div>
          </div>
          <div className="md:col-span-8">
            <h2 className="font-montserrat font-semibold text-3xl md:text-4xl leading-tight tracking-tight text-synergia-graphite max-w-2xl">
              {h.projectsTitle}
            </h2>
            <p className="mt-8 font-inter text-lg leading-relaxed text-synergia-graphite/75 max-w-3xl">
              {h.projectsBody}
            </p>
            <div className="mt-10">
              <Link to="/projetos" className="text-synergia-green font-montserrat font-semibold link-underline inline-flex items-center gap-2" data-testid="projects-link">
                {t.nav.projects} <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* DIFERENCIAIS - ICONES EM LINHA */}
      <section className="border-b border-synergia-line" data-testid="differentials-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mb-16">
            <div className="md:col-span-4">
              <div className="eyebrow">{h.diffEyebrow}</div>
            </div>
            <div className="md:col-span-8">
              <h2 className="font-montserrat font-semibold text-3xl md:text-4xl leading-tight tracking-tight text-synergia-graphite max-w-2xl">
                {h.diffTitle}
              </h2>
            </div>
          </div>

          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8 border-t border-synergia-line pt-14"
            data-testid="differentials-row"
          >
            {h.diffs.map((d, i) => {
              const Icon = [Network, Layers, ShieldCheck, Users][i] || Network;
              return (
                <div
                  key={d.title}
                  className="flex flex-col"
                  data-testid={`diff-${i}`}
                >
                  <div
                    className="w-14 h-14 border border-synergia-green/30 flex items-center justify-center text-synergia-green"
                    aria-hidden="true"
                  >
                    <Icon size={24} strokeWidth={1.5} />
                  </div>
                  <h3 className="mt-8 font-montserrat font-semibold text-lg md:text-xl text-synergia-green leading-snug">
                    {d.title}
                  </h3>
                  <div className="h-[2px] w-10 bg-synergia-gold mt-6" />
                  <p className="mt-6 font-inter text-[15px] leading-relaxed text-synergia-graphite/75">
                    {d.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SOBRE A SYNERGIA */}
      <section className="border-b border-synergia-line" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-4">
            <div className="eyebrow">{h.aboutEyebrow}</div>
          </div>
          <div className="md:col-span-8">
            <h2 className="font-montserrat font-semibold text-3xl md:text-4xl leading-tight tracking-tight text-synergia-graphite max-w-2xl">
              {h.aboutTitle}
            </h2>
            <p className="mt-8 font-inter text-lg leading-relaxed text-synergia-graphite/75 max-w-3xl">
              {h.aboutBody}
            </p>
            <div className="mt-10">
              <Link to="/sobre" className="text-synergia-green font-montserrat font-semibold link-underline inline-flex items-center gap-2" data-testid="about-link">
                {t.nav.about} <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* BLOCO FINAL */}
      <section className="bg-synergia-green text-white" data-testid="final-cta-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-28 md:py-36">
          <h2 className="font-montserrat font-semibold text-3xl md:text-5xl leading-tight tracking-tight max-w-3xl text-balance">
            {h.finalTitle}
          </h2>
          <div className="mt-10 h-[2px] w-20 bg-synergia-gold" />
          <p className="mt-8 font-inter text-lg leading-relaxed text-white/80 max-w-2xl">
            {h.finalBody}
          </p>
          <div className="mt-10">
            <Link to="/contato" className="btn-primary" data-testid="final-cta-button">
              {h.finalCta} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
